import { Component, inject, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../../modules/material.module';
import { AuthService } from '../../../../authentication/auth.service';
import { FormsModule } from '@angular/forms';
import { InputSelectComponent } from './components/input-select/input-select.component';
import {
  generateColorFromString,
  generateHistoryRecordText,
} from '../utils/comman';
import { TagInputComponent } from './components/tag-input/tag-input.component';
import { HighlightTextPipe } from '../../../../shared/pipe/highlight-text.pipe';
import {
  HISTORY_HIGHLIGHT_TYPES,
  APP_CONFIG,
  PRIORITIES,
  STATUS_TYPES,
  TASK_TYPES,
} from '../utils/constant';
import { TreeItemService } from '../../../../shared/services/tree-item.service';
import { uniqBy, uniq } from 'lodash';
import { TreeEventType } from '../../../../shared/utils/enums';
import { AccountService } from '../../../../shared/services/account.service';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { Attachment, NodeItem } from '../../../../shared/utils/types';
import { FileUploadService } from '../../../../shared/services/file-upload.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertDialogComponent } from '../../../../shared/components/alert-dialog/alert-dialog.component';
import { ThumbnailComponent } from "../../../../shared/components/thumbnail/thumbnail.component";
import {MatDatepickerModule} from '@angular/material/datepicker';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { AlertService } from '../../../../shared/services/alert.service';
import moment from 'moment';
export interface Option {
  name: string;
}

@Component({
  selector: 'app-item-form-dialog',
  imports: [
    MaterialModule,
    FormsModule,
    InputSelectComponent,
    TagInputComponent,
    HighlightTextPipe,
    MatProgressSpinnerModule,
    ThumbnailComponent,
    MatDatepickerModule
  ],
  providers: [
    provideNativeDateAdapter()
  ],
  templateUrl: './item-form-dialog.component.html',
  styleUrl: './item-form-dialog.component.scss',
  standalone: true,
})
export class ItemFormDialogComponent {
  private auth = inject(AuthService);
  private treeItemService = inject(TreeItemService);
  readonly dialogRef = inject(MatDialogRef<ItemFormDialogComponent>);
  private readonly data = inject<{itemBeingEdited: NodeItem}>(MAT_DIALOG_DATA);
  protected accountService = inject(AccountService);
  private dialog = inject(MatDialog);
  protected fileUploadService = inject(FileUploadService);
  private dateAdapter = inject(DateAdapter<Date>);
  private alertService = inject(AlertService);
  itemBeingEdited: NodeItem;

  addSharePermissionMessage = signal('');
  isHistoryExpanded = signal<boolean>(false);
  private suggestedEmails = signal<Array<string>>([]);
  typesOptions = signal<Option[]>(TASK_TYPES);
  statusTypes = signal<Option[]>(STATUS_TYPES);
  priorities = signal<Option[]>(PRIORITIES);
  allSuggestedTags = signal<Array<string>>([]);
  attachmentFiles: Attachment[] = [];

  types = HISTORY_HIGHLIGHT_TYPES.map((type) => type.name);
  today = new Date();

  ngOnInit(): void {
    this.itemBeingEdited = this.data.itemBeingEdited;
    this.updateSuggestedTypes();
    this.attachmentFiles = [...this.itemBeingEdited?.attachments || []];
    // Set the date format to DD/MM/YYYY
    this.dateAdapter.setLocale('en-GB');

    this.dialogRef.keydownEvents().subscribe((event) => {
      // if the escape key is pressed, close the dialog
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  onTypeSelected(event: any, name: string): void {
    this.itemBeingEdited[name] = event.name;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onDelete(): void {
    this.treeItemService.setTreeEvent({
      type: TreeEventType.MoveToTrash,
      data: this.itemBeingEdited.id,
    });
    this.onClose();
  }

  onSave(): void {

    // Get the files to upload that are not already in the itemBeingEdited.attachments array
    const files = this.attachmentFiles.filter(item => item.file).map(item => item.file);

    if (files.length) {
      this.fileUploadService.isFileUploading.set(true);
      // Upload files and save item only after all uploads complete
      this.fileUploadService.upload(this.itemBeingEdited.id, files).subscribe({
        next: (responses) => {
          // Save the item immediately after all files are uploaded
          const attachments = this.itemBeingEdited?.attachments || [];
          this.itemBeingEdited.attachments = [...attachments, ...responses];
          this.processDates();
          
          this.dialogRef.close(); // Close the dialog after files are uploaded 
          this.saveItem();
          this.fileUploadService.isFileUploading.set(false);
        },
        error: (error) => {
          console.error("Error uploading files", error);
          this.fileUploadService.isFileUploading.set(false);
        }
      });
    } else {
      // If no files to upload, save the item immediately
      if (this.itemBeingEdited?.attachments && (this.itemBeingEdited?.attachments.length > this.attachmentFiles.length)) {
        this.itemBeingEdited.attachments = [...this.attachmentFiles];
      }
      this.processDates();
      this.dialogRef.close();
      this.saveItem();
    }
  }

  // Helper method to process dates
  private processDates(): void {
    // Convert from Date object to string in YYYY-MM-DD format
    if (this.itemBeingEdited.start_date) {
      this.itemBeingEdited.start_date = moment(this.itemBeingEdited.start_date).format('YYYY-MM-DD');
    }
    if (this.itemBeingEdited.due_date) {
      this.itemBeingEdited.due_date = moment(this.itemBeingEdited.due_date).format('YYYY-MM-DD');
    }
  }

  removeFile(index: number) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: 'Are you sure you want to delete this file?',
        primaryButtonText: 'Ok',
        secondaryButtonText: 'Cancel',
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.attachmentFiles.splice(index, 1);
        if(this.itemBeingEdited.attachments.length > index) {
          this.itemBeingEdited.attachments.splice(index, 1);
        }
      }
    });
  }

  private saveItem(): void {
    this.treeItemService.setTreeEvent({
      type: TreeEventType.SaveItem,
      data: structuredClone(this.itemBeingEdited),
    });
  }

  openFileUploadDialog(): void {
    const dialogRef = this.dialog.open(FileUploadComponent, {
      data: {
        attachmentFiles: this.attachmentFiles,
      },
      width: '400px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.attachmentFiles = [...this.attachmentFiles, ...result.files]; // Store uploaded files
      }
    });
  }

  generateColorFromString(str: string) {
    return generateColorFromString(str);
  }

  hideHistory(index: number) {
    return index >= APP_CONFIG.NO_OF_EVENTS_IN_RECENT_HISTORY;
  }

  /**
   * returns whether the current user is a subscriber of the item being edited
   */
  isSubscriber(): boolean {
    const userEmail = this.auth.getCurrentUser()?.email;
    const itemSubscribers = this.itemBeingEdited['subscribers'];
    const isSubscriber = itemSubscribers.indexOf(userEmail) != -1;
    return isSubscriber;
  }

  generateHistoryRecordText(record: any) {
    const historyRecord = generateHistoryRecordText(record).split('::');

    return {
      date: historyRecord[0],
      text: historyRecord[1],
    };
  }

  /**
   * given a user email, returns whether it is the email of the currently logged in user
   */
  isCurrentUser(email: string): boolean {
    const currentUserEmail = this.auth.getCurrentUser().email;
    return email == currentUserEmail;
  }

  /**
   * returns whether this email is included or has been added to this item's list of assignees within its edit form
   */
  isAssigneeEdit(userEmail: string): boolean {
    const itemAssignees = this.itemBeingEdited.assignees;

    for (const thisAssignee of itemAssignees) {
      if (userEmail == thisAssignee) {
        return true;
      }
    }
    return false;
  }

  /**
   * Called when the start date changes to ensure due date is not before start date
   */
  startDateChanged(): void {
    // If due date exists and is before the newly selected start date, reset it
    if (this.itemBeingEdited.due_date && 
        new Date(this.itemBeingEdited.due_date) < new Date(this.itemBeingEdited.start_date)) {
      this.itemBeingEdited.due_date = null;
    }
  }

  /**
   * given a user email as well as whether that email is an assignee, adjust whether the user email is an assignee to be the opposite of what it is currently (triggered by toggling the "is assignees" checkbox)
   */
  toggleWhetherAssignee(userEmail: string, isCurrentlyAssignee: boolean): void {
    if (isCurrentlyAssignee) {
      // remove from assignees
      const currentAssignees = this.itemBeingEdited.assignees;
      const index = currentAssignees.indexOf(userEmail);
      if (index > -1) {
        currentAssignees.splice(index, 1);
      }
    } else {
      // add to assignees as well as add new explicity share permission corresponding to this email
      this.itemBeingEdited.assignees.push(userEmail);
    }
  }

  /**
   * given a tree node, returns an array of those share permissions that the node has inherited from its ancestors that are not explicitly assigned to the node
   */
  getExplicitPermissions(node: any): any[] {
    const permission = node
      ? node['shared_with'].filter((p) => p.type == 'Explicit')
      : [];
    return permission;
  }

  /**
   * given a tree node, returns an array of those share permissions that the node has inherited from its ancestors that are not explicitly assigned to the node
   */
  getInheritedPermissions(node: any): any[] {
    const permission = node
      ? node['shared_with'].filter((p) => p.type !== 'Explicit')
      : [];
    return permission;
  }

  /**
   * given a user email as well as whether that email is an subscriber, adjust whether the user email is an subscriber to be the opposite of what it is currently (triggered by toggling the "is subscribers" checkbox)
   */
  toggleWhetherSubscriber(isCurrentlySubscriber: boolean): void {
    const userEmail = this.auth.getCurrentUser().email;

    if (isCurrentlySubscriber) {
      // remove from subscribers
      const currentSubscribers = this.itemBeingEdited.subscribers;
      const index = currentSubscribers.indexOf(userEmail);
      if (index > -1) {
        currentSubscribers.splice(index, 1);
      }
    } else {
      // add to subscribers as well as add new explicity share permission corresponding to this email
      this.itemBeingEdited.subscribers.push(userEmail);
    }
  }

  /**
   * show/hide the full history of this item
   */
  showHideFullHistory(): void {
    this.isHistoryExpanded.update((state) => !state);
  }

  /**
   * returns a boolean indicating whether this are additional events in this item's history (aside from those in "recent history") that are currently not displayed
   */
  additionalHistoryHidden(): boolean {
    return (
      this.itemBeingEdited.history.length >
      APP_CONFIG.NO_OF_EVENTS_IN_RECENT_HISTORY
    );
  }

  /**
   * adds a share permission to the item by showing a new available share permission html element
   */
  addSharePermission($event: any): void {
    this.itemBeingEdited.shared_with.push({
      id: -1,
      user: { id: -1, email: '', display_name: '' },
      type: 'Explicit',
    });
    this.addSharePermissionMessage.set(
      'New users, assignees will be notified by email (if they have a preview account).'
    );
  }

  /**
   * removes a share permission from an item's edit form as well as the user email from the list of the item's assignees and subscribers
   */
  removeSharePermission(permission: any): void {
    this.itemBeingEdited.shared_with = this.itemBeingEdited.shared_with.filter(
      (p) => p.user.email !== permission.user.email
    );

    // also remove user email from list of assignees
    const currentAssignees = this.itemBeingEdited.assignees;
    let index = currentAssignees.indexOf(permission.user.email);
    if (index > -1) {
      currentAssignees.splice(index, 1);
    }

    // also remove user email from list of subscribers
    const currentSubscribers = this.itemBeingEdited.subscribers;
    index = currentSubscribers.indexOf(permission.user.email);
    if (index > -1) {
      currentSubscribers.splice(index, 1);
    }
  }

  /**
   * given an item, return deduped, alphabetically ordered array of emails corresponding to all share permissions associated with item tree, minus those already in the current item's permissions (for populating suggested emails dropdown on share permission editing)
   */
  getSuggestedEmails(item: any): string[] {
    const currentPermissions = item?.shared_with?.map((p) => p.user.email);
    const emails = this.suggestedEmails()
      .filter((email) => !currentPermissions.includes(email))
      .sort();
    return emails;
  }

  /**
   * given a user email, add this email to item assignees and also generate a new share permission corresponding to this email specific for this item (triggered by adding a user with an inherited share permission as an assignee)
   */
  addUserAsAssigneeWithExplicitPerm(userEmail: string): void {
    const sharePermission = this.itemBeingEdited.shared_with.filter(
      (p) => p.user.email == userEmail
    )[0];
    sharePermission.type = 'Explicit';
    this.itemBeingEdited.assignees.push(userEmail);
  }

  displayFn(option: string): string {
    return option || '';
  }

   /**
   * get list of all emails from item's and it's children share permission
   */
  private updateSuggestedTypes(): void {
    let types = [];
    let status = [];
    let priorities = [];
    let tags = [];
    let emails = []
    const itemsArray = this.treeItemService.allTreeNodeItems().filter((i) => !i.in_trash);
    itemsArray.forEach((item) => {
      this.updateSuggestedOptionsHelper(item, types, status, priorities, tags, emails);
    });

    this.updateOptions(this.typesOptions, types);
    this.updateOptions(this.statusTypes, status);
    this.updateOptions(this.priorities, priorities);
    // all suggested tags from active items 
    this.allSuggestedTags.set(uniq(tags));
    this.suggestedEmails.set(uniq(emails));
  }

  /**
   * helper function to get suggested options
   */
  private updateSuggestedOptionsHelper(item: any, typesSoFar: Option[], statusSoFar: Option[], prioritiesSoFar: Option[], tags: string[], emailsSoFar: string[]): void {
    if (item.type) {
      typesSoFar.push({ 'name': item.type });
    }
    if (item.priority) {
      prioritiesSoFar.push({ 'name': item.priority });
    }
    if (item.status) {
      statusSoFar.push({ 'name': item.status });
    }
    if(item.tags.length) {
      tags.push(...item.tags);
    }

    if(item.shared_with.length) {
      const permissions = item.shared_with?.map((p) => p.user.email) || [];
      emailsSoFar.push(...permissions);
    }

    const children = this.getChildren(item.id);
    for (const child of children) {
      this.updateSuggestedOptionsHelper(child, typesSoFar, statusSoFar, prioritiesSoFar, tags, emailsSoFar);
    }
  }

  private getChildren(parentId) {
    return this.treeItemService.allTreeNodeItems().filter((n) => n.parent_id === parentId);
  }

  /**
  * Deduplicates and updates the observable store with new options.
  */
  private updateOptions(store: WritableSignal<Option[]>, newOptions: Option[]): void {
    const uniqueNewOptions = uniqBy(newOptions, 'name');
    store.update((currentState: Option[]) => {
      return uniqBy([...currentState, ...uniqueNewOptions], option => option.name.toLowerCase());
    });
  }

  /**
   * Validates the size input, ensuring it's not negative
   * @param event Input event
   */
  validateSizeInput(event: any): void {
    const inputValue = event.target.value;
    
    if (inputValue < 0) {
      // Display alert
      this.alertService.showAlert({
        title: 'Invalid Input',
        message: 'Size must be a non-negative number (0 or greater). Valid examples: 0, 1, 1.3, 3.79, 1000.',
        secondaryButtonText: 'OK'
      });
    }
  }
}
